import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock5 } from 'lucide-react';
import { useAppDispatch } from '../../../redux/store';
import { setTopNavNameTitle } from '../../../redux/features/mobileLayout';

interface Breaktime {
  start?: Date;
  end?: Date;
  hours?: number;
  is_paid?: boolean;
  start_datetime?: Date;
  end_datetime?: Date;
  difference?: string;
}

interface LocationState {
  breaktimes: Breaktime[];
  recordId: string;
  employeeId?: string;
  parentRoute: string;
}

const BreakDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { breaktimes, recordId, employeeId, parentRoute } = location.state as LocationState;

  useEffect(() => {
    dispatch(setTopNavNameTitle('Break Details'));
  }, [dispatch]);

  const handleBackClick = () => {
    // Navigate back to the timecard record detail page
    if (parentRoute === 'timecard-record-detail') {
      navigate(-1);
    } else {
      navigate('/timecard-record-detail/' + recordId, { 
        state: { employeeId }
      });
    }
  };

  // Format time from Date object or string to 12-hour format (12:30 PM)
  const formatTime = (dateTime: Date | string | undefined): string => {
    if (!dateTime) return 'N/A';
    
    const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
    
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Format duration in minutes to hours and minutes (1h 30m)
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Calculate duration between start and end times in minutes
  const calculateDuration = (start: Date | string | undefined, end: Date | string | undefined): number => {
    if (!start || !end) return 0;
    
    const startDate = typeof start === 'string' ? new Date(start) : start;
    const endDate = typeof end === 'string' ? new Date(end) : end;
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
    
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  };

  return (
    <div className="h-full bg-white">
      <div className="container mx-auto bg-white mx-auto md:w-1/2">
        
        {/* Break list */}
        <div className="px-4 pt-2">
          {breaktimes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No breaks recorded</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {breaktimes.map((breaktime, index) => {
                const startTime = breaktime.start_datetime || breaktime.start;
                const endTime = breaktime.end_datetime || breaktime.end;
                const duration = breaktime.difference 
                  ? parseInt(breaktime.difference) 
                  : calculateDuration(startTime, endTime);
                
                return (
                  <li key={index} className="py-4">
                    <div className="flex justify-between">
                      <div className="flex items-center">
                        <Clock5 size={16} color="#2563eb" strokeWidth={1.5} className="mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Break {index + 1} - {breaktime.is_paid ? 'Paid' : 'Unpaid'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {startTime && endTime 
                              ? `${formatTime(startTime)} - ${formatTime(endTime)}`
                              : null}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-red-500">
                        {breaktime.difference}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreakDetailsPage;
